'use client';

import { Badge, Button } from '@common/components';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { useBenefitGate } from '../../_hooks/useBenefitGate';
import {
  useAttendance,
  useCheckInAttendance,
  useIsCheckingInAttendance,
} from '../_hooks/useAttendance';
import { AttendanceCloverStrip } from './AttendanceCloverStrip';

type TriggerGate = ReturnType<typeof useBenefitGate>['triggerGate'];

interface AttendanceCheckSectionProps {
  isGuest: boolean;
}

export const AttendanceCheckSection = ({
  isGuest,
}: AttendanceCheckSectionProps) => {
  const { triggerGate, gateModals } = useBenefitGate();

  return (
    <section className="flex flex-col gap-16 bg-Gray-white p-20 xl:rounded-medium xl:border xl:border-Gray-100">
      {isGuest ? (
        <GuestAttendanceCheck triggerGate={triggerGate} />
      ) : (
        <MemberAttendanceCheck triggerGate={triggerGate} />
      )}
      {gateModals}
    </section>
  );
};

const GuestAttendanceCheck = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => (
  <>
    <div className="flex flex-col items-center gap-16">
      <Badge className="bg-O-25 text-O-600">모바일 앱 전용 혜택</Badge>
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="font-700 text-Gray-900 typo-h7">
          매일 미션 참여하고 포인트 받기
        </h2>
        <p className="text-Gray-600 typo-body4">
          웰로 앱에서만 만날 수 있는 혜택을 살펴보세요
        </p>
      </div>
    </div>
    <AttendanceCloverStrip variant="guest" />
    <Button
      className="w-full"
      dimensions="H48"
      onClick={() => triggerGate(['login'], () => {})}
    >
      혜택받기
    </Button>
  </>
);

const AttendanceCurrentDay = () => {
  const { data } = useAttendance();

  return <>{data.todayDayIndex}</>;
};

const MemberCloverStrip = () => {
  const { data } = useAttendance();

  return <AttendanceCloverStrip cloverDays={data.cloverDays} />;
};

const BUTTON_LABEL_BY_STATE = {
  CHECK: '출석체크하기',
  RESTART: '1일부터 다시하기',
  DONE: '출석완료',
} as const;

const AttendanceCheckInButton = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => {
  const { data } = useAttendance();
  const { mutate: checkIn } = useCheckInAttendance();
  const isCheckingIn = useIsCheckingInAttendance();

  const handleCheckIn = () =>
    triggerGate(['login', 'adult', 'consent'], () => checkIn({}));

  return (
    <Button
      className="w-full"
      dimensions="H48"
      disabled={
        data.buttonState === 'DONE' || data.alreadyCheckedToday || isCheckingIn
      }
      onClick={handleCheckIn}
    >
      {BUTTON_LABEL_BY_STATE[data.buttonState]}
    </Button>
  );
};

const MemberAttendanceCheck = ({
  triggerGate,
}: {
  triggerGate: TriggerGate;
}) => (
  <>
    <div className="flex flex-col items-center gap-16">
      <Badge className="bg-O-25 text-O-600">
        <ErrorBoundary fallback={<>-</>}>
          <Suspense fallback="-">
            <AttendanceCurrentDay />
          </Suspense>
        </ErrorBoundary>
        일차 진행중
      </Badge>
      <div className="flex flex-col items-center gap-4 text-center">
        <h2 className="font-700 text-Gray-900 typo-h7">
          연속 출석하고 행운권 받기
        </h2>
        <p className="text-Gray-600 typo-body4">7일 연속 방문 시 행운권 3배</p>
      </div>
    </div>
    <ErrorBoundary fallback={<AttendanceCloverStrip />}>
      <Suspense fallback={<AttendanceCloverStrip />}>
        <MemberCloverStrip />
      </Suspense>
    </ErrorBoundary>
    <ErrorBoundary
      fallback={
        <Button className="w-full" dimensions="H48" disabled>
          출석체크하기
        </Button>
      }
    >
      <Suspense
        fallback={
          <Button className="w-full" dimensions="H48" disabled>
            출석체크하기
          </Button>
        }
      >
        <AttendanceCheckInButton triggerGate={triggerGate} />
      </Suspense>
    </ErrorBoundary>
  </>
);
